import { useCallback, useEffect, useRef, useState } from 'react';
import { openUrlInNewTab } from '@/shared/lib';
import { type AttachmentFileSource, getAttachmentSourceKind } from '../lib/attachmentSource';
import { useLazyConversationAttachmentFile, useLazyConversationAttachmentSignedUrl } from './api/hooks';

/** Адрес файла вложения и его получение */
export type AttachmentUrl = {
  /** Адрес, по которому файл открывается; пока не получен — `null` */
  url: string | null;
  /** Адрес получается */
  isLoading: boolean;
  /** Файл получить нельзя — источника нет либо получить не удалось */
  isUnavailable: boolean;
  /** Открыть файл в новой вкладке, получив его адрес; полученный раньше берётся сразу, не полученный — не открывается */
  openInNewTab: () => Promise<void>;
};

/**
 * Адрес файла вложения по его источнику ({@link getAttachmentSourceKind}): внешняя ссылка — как
 * есть, файл внутреннего хранилища — подписанной ссылкой, файл локального хранилища — `blob:`-адресом
 * полученного файла, который освобождается вместе с компонентом.
 *
 * @param isEager - получить адрес сразу (превью изображения), а не по нажатию (файл)
 */
export const useAttachmentUrl = (attachment: AttachmentFileSource, isEager: boolean): AttachmentUrl => {
  const sourceKind = getAttachmentSourceKind(attachment);
  const { id: attachmentId, url: attachmentUrl } = attachment;
  const { refetch: fetchSignedUrl } = useLazyConversationAttachmentSignedUrl();
  const { refetch: fetchFile } = useLazyConversationAttachmentFile();

  const [fetchedUrl, setFetchedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const isUnmountedRef = useRef(false);
  // Идущее получение: повторный вызов ждёт его, а не запускает второе
  const pendingRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
  }, []);

  const directUrl = sourceKind === 'external' ? (attachmentUrl ?? null) : null;

  const resolveUrl = useCallback(async (): Promise<string | null> => {
    if (sourceKind === 'unavailable') return null;
    if (sourceKind === 'external') return attachmentUrl ?? null;
    if (fetchedUrl) return fetchedUrl;
    if (pendingRef.current) return pendingRef.current;

    const fetchUrl = async (): Promise<string | null> => {
      if (sourceKind === 'signed') {
        const { url } = await fetchSignedUrl(attachmentId);
        // Ссылки в ответе нет — файл получить не удалось
        if (!url) throw new Error(`Нет подписанной ссылки на вложение ${attachmentId}`);
        return url;
      }
      const file = await fetchFile(attachmentId);
      // Компонент уже снят — адрес некому освободить, поэтому он и не создаётся
      if (isUnmountedRef.current) return null;
      const objectUrl = URL.createObjectURL(file);
      objectUrlRef.current = objectUrl;
      return objectUrl;
    };

    setIsLoading(true);
    pendingRef.current = fetchUrl()
      .then((url) => {
        setFetchedUrl(url);
        return url;
      })
      .catch(() => {
        setIsFailed(true);
        return null;
      })
      .finally(() => {
        pendingRef.current = null;
        setIsLoading(false);
      });
    return pendingRef.current;
  }, [sourceKind, attachmentId, attachmentUrl, fetchedUrl, fetchSignedUrl, fetchFile]);

  useEffect(() => {
    if (isEager && !isFailed) void resolveUrl();
  }, [isEager, isFailed, resolveUrl]);

  const openInNewTab = useCallback(async () => {
    const url = await resolveUrl();
    if (url) openUrlInNewTab(url);
  }, [resolveUrl]);

  return {
    url: directUrl ?? fetchedUrl,
    isLoading,
    isUnavailable: sourceKind === 'unavailable' || isFailed,
    openInNewTab,
  };
};
